import { useQuery } from '@tanstack/react-query'
import * as React from 'react'

import groupService from '@/services/group.service'

import { Channel, GroupChannel } from '@/types/group-channel.type'

type SidebarGroup = GroupChannel & { isExpanded: boolean }

export function useGroups(currentGroupId?: string) {
  const [expandedGroups, setExpandedGroups] = React.useState<Set<string>>(new Set())

  const {
    data: groupsData,
    isLoading: groupsLoading,
    error: groupsQueryError,
  } = useQuery({
    queryKey: ['forum-groups'],
    queryFn: () => groupService.getGroups(),
    staleTime: 5 * 60 * 1000, // 5 minutes
    refetchOnWindowFocus: false,
  })

  const {
    data: allChannelsData,
    isLoading: channelsLoading,
    error: channelsQueryError,
  } = useQuery({
    queryKey: ['forum-channels'],
    queryFn: () => groupService.getChannels(),
    staleTime: 5 * 60 * 1000, // 5 minutes
    refetchOnWindowFocus: false,
  })

  const loading = groupsLoading || channelsLoading
  const queryError = groupsQueryError || channelsQueryError
  const error = queryError ? (queryError instanceof Error ? queryError.message : 'Failed to load data') : null

  const ungroupedChannels: Channel[] = React.useMemo(() => {
    if (!allChannelsData || !groupsData) return []

    const groupedChannelIds = new Set(
      groupsData.flatMap((group: GroupChannel) => (group.channels || []).map((channel: Channel) => channel.id))
    )

    return allChannelsData
      .filter((channel: Channel) => !groupedChannelIds.has(channel.id))
      .sort((a: Channel, b: Channel) => a.order - b.order)
  }, [allChannelsData, groupsData])

  const groups: SidebarGroup[] = React.useMemo(() => {
    if (!groupsData) return []
    return groupsData
      .sort((a: GroupChannel, b: GroupChannel) => a.order - b.order)
      .map((g: GroupChannel) => ({
        ...g,
        channels: (g.channels || []).sort((a: Channel, b: Channel) => a.order - b.order),
        isExpanded: expandedGroups.has(g.id),
      }))
  }, [groupsData, expandedGroups])

  React.useEffect(() => {
    if (groupsData && expandedGroups.size === 0) {
      if (currentGroupId) {
        setExpandedGroups(new Set([currentGroupId]))
      } else {
        const allGroupIds = new Set(groupsData.map((g: GroupChannel) => g.id))
        setExpandedGroups(allGroupIds)
      }
    }
  }, [groupsData, expandedGroups.size, currentGroupId])

  const toggleGroup = React.useCallback((groupId: string) => {
    setExpandedGroups(prev => {
      const newSet = new Set(prev)
      if (newSet.has(groupId)) {
        newSet.delete(groupId)
      } else {
        newSet.add(groupId)
      }
      return newSet
    })
  }, [])

  return {
    groups,
    ungroupedChannels,
    loading,
    error,
    toggleGroup,
  }
}
